import { lazy, type ComponentType } from "react";

/**
 * Wrapper around React.lazy that recovers from "stale deploy" chunk-load
 * failures. When a new version is published, the previous build's hashed page
 * files (e.g. `sign-up-DMaO9c6h.js`) no longer exist, so a user who still has
 * the old page open gets "Failed to fetch dynamically imported module" when
 * they navigate. The fix is to reload once so the browser fetches the new
 * files.
 *
 * Loop safety: we record the last reload so a genuinely broken chunk reloads at
 * most once and then rethrows to the ErrorBoundary instead of looping. The
 * marker is stored in sessionStorage when available, and falls back to a URL
 * query sentinel (which survives a reload without needing storage permissions)
 * when sessionStorage is blocked (e.g. some private-browsing modes).
 */
const RELOAD_TS_KEY = "chunk-reload-ts";
const RELOAD_COOLDOWN_MS = 10_000;
const URL_SENTINEL = "__chunkReload";

export function isChunkLoadError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|dynamically imported module/i.test(
    message,
  );
}

/** Epoch ms of the last recorded reload, or 0 if none / unknown. */
function readLastReload(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_TS_KEY) || 0);
  } catch {
    // Storage blocked: a present URL sentinel means we already retried this
    // navigation, so report "just now" to keep us inside the cooldown.
    try {
      const url = new URL(window.location.href);
      return url.searchParams.has(URL_SENTINEL) ? Date.now() : 0;
    } catch {
      return 0;
    }
  }
}

function markReload(): void {
  try {
    window.sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
  } catch {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set(URL_SENTINEL, "1");
      window.history.replaceState(window.history.state, "", url.toString());
    } catch {
      // nothing else we can do — the reload itself is still the best effort
    }
  }
}

/** Returns true if we triggered a reload, false if the caller should rethrow. */
export function reloadForStaleChunk(): boolean {
  if (Date.now() - readLastReload() > RELOAD_COOLDOWN_MS) {
    markReload();
    window.location.reload();
    return true;
  }
  return false;
}

export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (isChunkLoadError(err) && reloadForStaleChunk()) {
        // Keep the Suspense fallback up while the page reloads.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
